import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import ButtonBase from '@material-ui/core/ButtonBase';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import LazyAnimation from './LazyAnimation';
import ThemeList from './ThemeList';

const useStyles = makeStyles(theme => ({
  wrapper: {
    backgroundColor: '#F7F7F7',
    paddingTop: 81,
    paddingBottom: 81,
  },
  container: {
    width: '80%',
    maxWidth: 1080,
    margin: 'auto',
    // display: 'flex',
    // flexDirection: 'column',
  },
  header: {
    fontFamily: 'PoppinsSemiBold',
    fontWeight: '600',
    fontSize: 23,
    lineHeight: '1.5em',
    paddingBottom: 10,
    color: '#333333',
    marginBottom: 15,
  },
  body: {
    fontWeight: '300',
    fontSize: 16,
    lineHeight: '1.8em',
    paddingBottom: 10,
    marginBottom: '10%',
    fontFamily: 'MontserratLight',
    color: '#666666',
  },
  buttonWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  button: {
    color: '#FFFFFF',
    borderRadius: 3,
    letterSpacing: 2,
    textAlign: 'right',
    fontSize: 16,
    fontFamily: 'MontserratSemiBold',
    fontWeight: '600',
    textTransform: 'uppercase',
    backgroundColor: '#E09900',
    lineHeight: '1.7em',
    padding: '12px 1.5em',
    transition: 'all 300ms ease 0ms',
    position: 'relative',
    '& > svg': {
      position: 'absolute',
      opacity: 0,
      marginLeft: '-1em',
      transition: 'all 300ms ease 0ms',
      height: 25,
      right: 8,
    },
    '&:hover': {
      paddingRight: '2.5em',
      paddingLeft: '1.2em',
      borderRadius: 25,
      '& > svg': {
        opacity: 1,
        marginLeft: 0,
      },
    },
  },
  image: {
    width: '100%',
    maxWidth: 400,
    [theme.breakpoints.up('md')]: {
      marginTop: 100,
    },
  },
  themesContainer: {
    position: 'relative',
  },
}));

function ResourcesSection({ onPageExit }) {
  const classes = useStyles();

  const themes = [
    {
      title: 'Mental Health',
      list: [
        'Addiction & Recovery',
        'ADHD',
        'Anxiety',
        'Bipolar',
        'Body Image',
        'Depression',
        'Eating Behaviors',
        'Stress',
        'Therapy &  Support Groups',
        'Trauma',
      ],
    },
    {
      title: 'Life Events',
      list: [
        'Adolescent Anxiety (Anxiety in Teens)',
        'Adolescent Depression (Depression in Teens)',
        'Breakups',
        'Bullying',
        'Caregiver Support: Elderly',
        'Divorce',
        'End-of-Life Care',
        'Housing Transitions',
        'Job Transitions',
        'Menopause',
      ],
    },
    {
      title: 'Emotional Wellbeing',
      list: [
        'Anger Management',
        'Creative Expression',
        'Emotional Intelligence',
        'Grief and Loss',
        'LGBTQ+',
        'Nature & The Outdoors',
        'Parenting',
        'Relationships (Romantic)',
        'Service & Volunteering',
        'Time Management',
      ],
    },
    {
      title: 'Physical Wellbeing',
      list: [
        'Aging & Longevity',
        'Bodywork',
        'Cannabinoids (CBD)',
        'Caregiver Support: General',
        'Exercise & Body Movement',
        'Herbs and Supplements',
        'Living with Cancer',
        'Living with Chronic Illness',
        'Nutrition',
        'Sleep',
      ],
    },
    {
      title: 'Spiritual Wellbeing',
      list: [
        'Breathwork',
        'Hypnosis',
        'Inquiry & Reflection',
        'Meaning & Purpose',
        'Meditation',
        'Mindfulness',
        'Psychedelics and Entheogens',
        'Self-Transcendence',
        'Spirituality',
        'Yoga',
      ],
    },
    {
      title: 'Positive Psychology',
      list: [
        'Compassion',
        'Forgiveness',
        'Happiness',
        'Humor, Laughter, & Comedy',
        'Meaningful Socializing & Loneliness',
        'Negative Thinking',
        'Positive Psychology',
        'Resilience',
        'Vulnerability',
        'Work-Life Balance',
      ],
    },
  ];

  return (
    <section id="resources-section">
      <div className={classes.wrapper}>
        <div className={classes.container}>
          <Grid container spacing={8}>
            <Grid item xs={12} md={4}>
              <LazyAnimation type="translate-right-40">
                <Typography className={classes.header}>
                  Over 200 Topic Areas and 20,000 Evidence-Based Resources
                </Typography>
                <Typography className={classes.body}>
                  Now you can provide a highly accessible gateway to help
                  individuals flourish. At the heart of CredibleMind is our
                  cloud-based content engine that’s exceptionally easy to use.
                  From emotional, physical and spiritual wellbeing to mental
                  health, life events, and positive psychology, users are
                  empowered with trusted information. And through machine
                  learning, these resources stay continually updated.
                </Typography>
              </LazyAnimation>
              <LazyAnimation type="translate-right-40 fade-in">
                <div className={classes.buttonWrapper}>
                  <ButtonBase
                    component="a"
                    href="https://benovo.crediblemind.com"
                    target="_blank"
                    disableRipple
                    classes={{ root: classes.button }}
                    onClick={() =>
                      onPageExit(
                        'https://benovo.crediblemind.com',
                        'resources-section',
                      )
                    }
                  >
                    See an example
                    <ChevronRightIcon />
                  </ButtonBase>
                </div>
              </LazyAnimation>
            </Grid>
            <Grid item xs={12} md={4}>
              <img
                className={classes.image}
                src="https://i1.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/sources-6.png?w=1080&amp;ssl=1"
                alt=""
                title=""
                srcSet="https://i1.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/sources-6.png?w=1054&amp;ssl=1 1054w, https://i1.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/sources-6.png?resize=282%2C300&amp;ssl=1 282w, https://i1.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/sources-6.png?resize=962%2C1024&amp;ssl=1 962w, https://i1.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/sources-6.png?resize=768%2C818&amp;ssl=1 768w, https://i1.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/sources-6.png?resize=980%2C1043&amp;ssl=1 980w, https://i1.wp.com/solutions.crediblemind.com/wp-content/uploads/2019/12/sources-6.png?resize=480%2C511&amp;ssl=1 480w"
              />
            </Grid>
            <Grid item xs={12} md={4} className={classes.themesContainer}>
              {themes.map(theme => (
                <ThemeList theme={theme} key={theme.title} />
              ))}
            </Grid>
          </Grid>
        </div>
      </div>
    </section>
  );
}

ResourcesSection.propTypes = {
  onPageExit: PropTypes.func,
};

export default ResourcesSection;
